import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PlansDownloadCta from '../components/PlansDownloadCta';
import { CityCardSkeletonGrid } from '../components/skeletons/CityCardSkeleton';
import { useSeoMeta } from '../hooks/useSeoMeta';
import { useJsonLd } from '../hooks/useJsonLd';
import { useLanguage } from '../contexts/LanguageContext';
import { ciudadesPageJsonLd } from '../seo/jsonLd';
import { getCityImageSources } from '../data/cityPhotos';
import { CITY_PREVIEW_BY_ID, FEATURED_CITY_IDS } from '../data/featuredCityPreviews';
import { getCities } from '../api/client';
import { OG_IMAGE_URL } from '../pages/home-redesign/cityLinks';
import { useHomeRedesignStrings } from '../pages/home-redesign/useHomeRedesignStrings';
import type { CityApi } from '../api/types';

function cityFromPreview(id: string): CityApi | undefined {
  const preview = CITY_PREVIEW_BY_ID[id];
  if (!preview) return undefined;
  return {
    id: preview.id,
    name: preview.name,
    photoId: '',
    imagePosition: preview.imagePosition,
    totalPlanes: 0,
    chicas: 0,
    planesToday: 0,
    tagline: preview.tagline,
  };
}

const cardBorder = 'border-[rgba(74,33,71,0.1)]';

const Ciudades: React.FC = () => {
  const { currentLanguage } = useLanguage();
  const { ciudadesPage } = useHomeRedesignStrings();
  const [cities, setCities] = useState<CityApi[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useSeoMeta({
    title: ciudadesPage.seoTitle,
    description: ciudadesPage.seoDescription,
    url: 'https://chisme.social/ciudades',
    image: OG_IMAGE_URL,
    locale: currentLanguage === 'en' ? 'en' : 'es',
  });

  const jsonLd = useMemo(() => ciudadesPageJsonLd(), []);
  useJsonLd(jsonLd);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    getCities()
      .then(({ cities: nextCities }) => {
        if (!active) return;
        const byId = new Map(nextCities.map((c) => [c.id, c]));
        const featured = FEATURED_CITY_IDS.map((id) => byId.get(id) ?? cityFromPreview(id)).filter(
          (c): c is CityApi => c != null
        );
        setCities(featured);
      })
      .catch((error) => {
        console.error('Failed to load cities:', error);
        if (!active) return;
        setCities([]);
        setLoadError(true);
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  return (
      <div className="container-chisme py-10 lg:py-16">
        <header className="mb-12 text-center">
          <h1 className="font-instrument text-4xl leading-tight tracking-[-0.02em] text-[#2a0f2a] sm:text-5xl md:text-6xl lg:text-[64px] lg:leading-[0.98]">
            <span className="block">Descubre planes</span>
            <span className="block italic text-chisme-primary">en tu ciudad</span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl font-jakarta text-lg leading-relaxed text-[#5a3158] md:text-xl">
            Miles de mujeres conectando y haciendo planes reales
          </p>
        </header>

        {loading ? (
          <CityCardSkeletonGrid />
        ) : loadError ? (
          <div className={`mx-auto max-w-lg rounded-3xl border ${cardBorder} bg-white px-6 py-16 text-center`}>
            <p className="font-jakarta text-xl text-[#2a0f2a] mb-3">No se pudieron cargar las ciudades</p>
            <p className="font-jakarta text-sm text-[#5a3158]">
              Comprueba tu conexión e inténtalo de nuevo en un momento
            </p>
          </div>
        ) : cities.length === 0 ? (
          <div className={`mx-auto max-w-lg rounded-3xl border ${cardBorder} bg-white px-6 py-16 text-center`}>
            <p className="font-jakarta text-xl text-[#2a0f2a]">Descarga la app para ver los planes cerca de ti</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:gap-7 lg:grid-cols-2">
            {cities.map((city, index) => {
              const { src, srcSet, imagePosition } = getCityImageSources(
                city.id,
                city.photoId,
                city.imagePosition,
              );
              return (
                <Link
                  key={city.id}
                  to={`/ciudades/${city.id}`}
                  className="group block rounded-3xl focus:outline-none focus-visible:ring-2 focus-visible:ring-chisme-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[#fcf7fa]"
                >
                  <article
                    className={`flex h-full min-h-[200px] flex-col overflow-hidden rounded-3xl border ${cardBorder} bg-white transition-colors duration-200 hover:border-chisme-primary/40 sm:min-h-[220px] sm:flex-row`}
                  >
                    <div className="relative aspect-[16/10] w-full shrink-0 bg-[#ebe4ea] sm:aspect-auto sm:min-h-[220px] sm:w-[42%] sm:min-w-[200px] sm:max-w-[300px]">
                      <img
                        src={src}
                        srcSet={srcSet}
                        sizes="(max-width: 639px) 100vw, (max-width: 1023px) 50vw, 300px"
                        alt={`Vista de ${city.name}`}
                        loading={index < 2 ? 'eager' : 'lazy'}
                        fetchPriority={index === 0 ? 'high' : undefined}
                        decoding="async"
                        style={{ objectPosition: imagePosition }}
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                      />
                    </div>

                    <div className="flex min-w-0 flex-1 flex-col justify-between p-6 sm:p-7">
                      <header className="space-y-1">
                        <h3 className="font-jakarta text-2xl font-bold leading-tight tracking-tight text-[#2a0f2a] sm:text-[1.65rem]">
                          {city.name}
                        </h3>
                        <p className="pt-0.5 font-jakarta text-sm leading-snug text-[#5a3158]">
                          {city.tagline}
                        </p>
                      </header>

                      <div className={`mt-5 grid grid-cols-3 gap-0 overflow-hidden rounded-2xl border ${cardBorder} bg-chisme-bg sm:mt-6`}>
                        <div className="border-r border-[rgba(74,33,71,0.1)] px-3 py-3.5 text-center sm:px-4 sm:py-4">
                          <p className="font-jetbrains text-[10px] font-medium uppercase leading-tight tracking-wide text-[#5a3158] sm:text-xs">
                            Planes
                          </p>
                          <p className="mt-1 font-jakarta text-lg font-bold tabular-nums text-[#2a0f2a] sm:text-xl">
                            {city.totalPlanes}
                          </p>
                        </div>
                        <div className="border-r border-[rgba(74,33,71,0.1)] px-3 py-3.5 text-center sm:px-4 sm:py-4">
                          <p className="font-jetbrains text-[10px] font-medium uppercase leading-tight tracking-wide text-[#5a3158] sm:text-xs">
                            Chicas
                          </p>
                          <p className="mt-1 font-jakarta text-lg font-bold tabular-nums leading-none text-[#2a0f2a] sm:text-xl">
                            {city.chicas.toLocaleString('es-ES')}
                          </p>
                        </div>
                        <div className="px-3 py-3.5 text-center sm:px-4 sm:py-4">
                          <p className="font-jetbrains text-[10px] font-medium uppercase leading-tight tracking-wide text-[#5a3158] sm:text-xs">
                            Hoy
                          </p>
                          <p className="mt-1 font-jakarta text-lg font-bold tabular-nums text-chisme-primary sm:text-xl">
                            {city.planesToday}
                          </p>
                        </div>
                      </div>

                      <div className="mt-5 flex items-center justify-between gap-3 border-t border-[rgba(74,33,71,0.1)] pt-4">
                        <span className="font-jakarta text-sm font-semibold text-chisme-primary group-hover:underline underline-offset-4 decoration-chisme-primary/40">
                          Ver planes en {city.name.split(' ')[0]}
                        </span>
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[rgba(74,33,71,0.1)] text-chisme-primary transition-colors duration-200 group-hover:border-chisme-primary/50 group-hover:bg-chisme-secondary/60">
                          <svg className="-mr-px h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </article>
                </Link>
              );
            })}
          </div>
        )}

        <PlansDownloadCta
          title="¿Y en tu ciudad? También."
          body="Chisme funciona en cualquier rincón del mundo. Descarga la app, descubre planes cerca de ti o propón el tuyo en un minuto."
        />
      </div>
  );
};

export default Ciudades;
